// Check if we're in browser environment
const isBrowser = () => typeof window !== 'undefined';

// Initialize TTQ if it doesn't exist
const initializeTTQ = () => {
  if (typeof window !== 'undefined' && !window.ttq) {
    window.ttq = window.ttq || [];
  }
};

// Clean object by removing null/undefined values
const cleanEventData = (data) => {
  return Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== null && value !== undefined)
  );
};

// Base event tracker
export const ttqTrackEvent = (eventName, eventData = {}) => {
  if (!isBrowser() || !window.ttq) {
    console.warn('TikTok Pixel: ttq not available');
    return;
  }
  
  const cleanEvent = cleanEventData(eventData);
  
  // Remove the dataLayer push - this is for GTM only
  window.ttq.track(eventName, cleanEvent);
  console.log('TikTok Pixel Event tracked:', eventName, cleanEvent);
};

// Track page views - TikTok automatically captures UTM parameters
export const ttqPageView = (additionalData = {}) => {
  if (!isBrowser() || !window.ttq) {
    console.warn('TikTok Pixel: ttq not available');
    return;
  }
  
  // TikTok automatically captures page URL, UTM parameters, referrer, etc.
  window.ttq.page(additionalData);
  console.log('TikTok Pixel Page View tracked:', additionalData);
};

// Track login
export const ttqTrackLogin = (user_id, method = 'email') => {
  ttqTrackEvent('Login', {
    content_id: user_id,
    content_type: 'user_login',
    method
  });
};

// Track user registration
export const ttqTrackRegistration = (user_id, method = 'email') => {
  ttqTrackEvent('CompleteRegistration', {
    content_id: user_id,
    content_type: 'user_registration',
    method
  });
};

// Track form submissions
export const ttqTrackFormSubmit = (form_name, form_step = null) => {
  ttqTrackEvent('SubmitForm', {
    content_name: form_name,
    content_category: 'form_submission',
    form_step
  });
};

// Track button clicks
export const ttqTrackButtonClick = (button_name, section = null) => {
  ttqTrackEvent('ClickButton', {
    content_name: button_name,
    content_category: 'button_click',
    section
  });
};

// NEW EVENTS BASED ON YOUR TABLE

// 1. Track user started registration (Layer 1)
export const ttqTrackUserStartedRegistration = (additionalData = {}) => {
  ttqTrackEvent('ViewContent', {
    content_type: 'registration_page',
    content_name: 'registration_started',
    page_path: '/register',
    ...additionalData,
    custom_event_name: 'user_started_registration',
  });
};

// 2a. Track account type selection (Layer 2a)
export const ttqTrackAccountTypeSelection = (account_type) => {
  ttqTrackEvent('SelectContent', {
    content_type: 'account_type',
    content_name: account_type, // 'personal' or 'corporate'
    custom_event_name: 'user_selected_account_type',
  });
};

// 2b. Track onboarding step progression (Layer 2b)
export const ttqTrackOnboardingStep = (step_name, step_number, account_type = null) => {
  ttqTrackEvent('AchieveLevel', {
    level: step_number,
    content_id: window.sessionStorage.getItem("register_user_email") || window.sessionStorage.getItem("register_user_id") || 'unknown',
    content_name: step_name,
    content_category: 'onboarding',
    account_type,
    custom_event_name: 'user_entered_onboarding_step',
  });
};

// 3a. Track KYC started (Layer 3a)
export const ttqTrackKYCStarted = (account_type) => {
  ttqTrackEvent('SubmitForm', {
    content_id: window.sessionStorage.getItem("register_user_email") || window.sessionStorage.getItem("register_user_id") || 'unknown',
    content_type: 'kyc_verification',
    content_name: account_type,
    custom_event_name: 'kyc_started'
  });
};

// 3b. Track KYC completed (Layer 3b)
export const ttqTrackKYCCompleted = (account_type) => {
  ttqTrackEvent('CompleteRegistration', {
    content_id: window.sessionStorage.getItem("register_user_email") || window.sessionStorage.getItem("register_user_id") || 'unknown',
    content_type: 'kyc_verification',
    content_name: account_type,
    custom_event_name: 'kyc_completed'
  });
};

// 3c. Track KYC failed (Layer 3c)
export const ttqTrackKYCFailed = (account_type, reason = null) => {
  ttqTrackEvent('ViewContent', {
    content_id: window.sessionStorage.getItem("register_user_email") || window.sessionStorage.getItem("register_user_id") || 'unknown',
    content_type: 'kyc_verification',
    content_name: account_type,
    reason,
    custom_event_name: 'kyc_completed'
  });
};

// 6. Track card created (Layer 6)
export const ttqTrackCardCreated = (additionalData = {}) => {
  ttqTrackEvent('AddPaymentInfo', {
    content_id: window.sessionStorage.getItem("register_user_email") || window.sessionStorage.getItem("register_user_id") || 'unknown',
    content_type: 'card_creation',
    content_name: 'card_created',
    custom_event_name: 'card_created',
    ...additionalData
  });
};

// 7. Track first transaction (Layer 7)
export const ttqTrackFirstTransaction = (additionalData = {}) => {
  ttqTrackEvent('Purchase', {
    content_id: window.sessionStorage.getItem("register_user_email") || window.sessionStorage.getItem("register_user_id") || 'unknown',
    content_type: 'first_transaction',
    content_name: 'user_first_transaction',
    value: additionalData.amount || 0,
    currency: additionalData.currency || 'USD',
    custom_event_name: 'first_transaction',
    ...additionalData
  });
};

// 8. Track account topup (Layer 8)
export const ttqTrackAccountTopup = (additionalData = {}) => {
  ttqTrackEvent('CompletePayment', {
    content_id: window.sessionStorage.getItem("register_user_email") || window.sessionStorage.getItem("register_user_id") || 'unknown',
    content_type: 'account_topup',
    content_name: 'card_topup_completed',
    value: additionalData.amount || 0,
    currency: additionalData.currency || 'USD',
    transaction_id: additionalData.transaction_id || 'unknown',
    custom_event_name: 'account_topup',
    ...additionalData
  });
};

// Generic event tracker for custom events
export const ttqTrackCustomEvent = (eventName, eventData = {}) => {
  ttqTrackEvent(eventName, eventData);
};

// Initialize TTQ on module load
initializeTTQ();